import type {
  TBillingRecord,
  TInvoiceCustomer,
  TBillingHistoryItem,
  TSubscriptionDetailItem,
} from '@/types/subscription';

import clsx from 'clsx';
import { useState } from 'react';

import { IconCheck, IconDownload } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox';
import EmptyState from '@/components/EmptyState';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';

import {
  formatCurrency,
  downloadInvoices,
  buildBillingRecord,
} from './helpers';

interface IPaidBadgeProps {
  label: string;
}

interface IBillingRowProps {
  row: TBillingHistoryItem;
  rowIndex: number;
  paidLabel: string;
  isChecked: boolean;
  viewOnly: boolean;
  isChildChecked: (id: string) => boolean;
  formatDate: (value: number) => string;
  onToggle: (key: string, checked: boolean) => void;
  onDownload: (item: TBillingHistoryItem | TSubscriptionDetailItem) => void;
}

interface IChildRowProps {
  detail: TSubscriptionDetailItem;
  isLast: boolean;
  paidLabel: string;
  isChecked: boolean;
  viewOnly: boolean;
  formatDate: (value: number) => string;
  onToggle: (key: string, checked: boolean) => void;
  onDownload: (item: TSubscriptionDetailItem) => void;
}

interface IBillingHistoryTableProps {
  billingHistory: TBillingHistoryItem[];
  customer: TInvoiceCustomer;
  viewOnly?: boolean;
}

const PAID_BADGE_CLASS =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-light-50 text-xs font-semibold text-purple-secondary';

const PaidBadge = ({ label }: IPaidBadgeProps) => (
  <span className={PAID_BADGE_CLASS}>
    <IconCheck size={16} />
    {label}
  </span>
);

const ChildRow = ({
  detail,
  isLast,
  paidLabel,
  isChecked,
  viewOnly,
  formatDate,
  onToggle,
  onDownload,
}: IChildRowProps) => (
  <tr
    className={clsx(
      'bg-neutral-light-50 h-11.5',
      !viewOnly && 'cursor-pointer'
    )}
    onClick={viewOnly ? undefined : () => onDownload(detail)}
  >
    {!viewOnly && (
      <td
        className="relative ps-12 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          aria-hidden
          className="absolute start-6 -top-px h-[calc(50%+1px)] w-4 border-s border-b border-neutral-light-200"
        />
        {!isLast && (
          <span
            aria-hidden
            className="absolute start-6 top-1/2 -bottom-px border-s border-neutral-light-200"
          />
        )}
        <Checkbox
          id={`invoice-child-${detail?.id}`}
          checked={isChecked}
          onChange={(checked) => onToggle(detail?.id, checked)}
        />
      </td>
    )}
    <td
      className={clsx(
        'py-2.5 text-sm text-neutral-primary',
        viewOnly ? 'ps-12 pe-4 relative' : 'px-4'
      )}
    >
      {viewOnly && (
        <>
          <span
            aria-hidden
            className="absolute start-6 -top-px h-[calc(50%+1px)] w-4 border-s border-b border-neutral-light-200"
          />
          {!isLast && (
            <span
              aria-hidden
              className="absolute start-6 top-1/2 -bottom-px border-s border-neutral-light-200"
            />
          )}
        </>
      )}
      {detail?.invoiceName}
    </td>
    <td className="px-4 py-2.5 text-sm text-neutral-primary">
      {formatCurrency(detail.amount, detail.currency)}
    </td>
    {!viewOnly && (
      <td className="px-4 py-2.5 text-sm text-neutral-primary">
        {detail?.student_limit}
      </td>
    )}
    <td className="px-4 py-2.5 text-sm text-neutral-primary">
      {formatDate(detail?.start_date)}
    </td>
    <td className="px-4 py-2.5">
      <PaidBadge label={paidLabel} />
    </td>
    {!viewOnly && (
      <td className="px-4 py-2.5 text-center">
        <Button
          variant="unstyled"
          className="p-1 hover:bg-neutral-light-100 rounded cursor-pointer mx-auto"
          icon={<IconDownload size={20} className="text-neutral-secondary" />}
          onClick={(e) => {
            e.stopPropagation();
            onDownload(detail);
          }}
        />
      </td>
    )}
  </tr>
);

const BillingRow = ({
  row,
  rowIndex,
  paidLabel,
  isChecked,
  viewOnly,
  isChildChecked,
  formatDate,
  onToggle,
  onDownload,
}: IBillingRowProps) => {
  const rowKey = `row-${rowIndex}`;
  const children = row.subscriptionDetails ?? [];

  return (
    <>
      <tr
        className={clsx(
          'border-b border-neutral-light-200 last:border-b-0 h-16',
          !viewOnly && 'cursor-pointer hover:bg-brand-light-50'
        )}
        onClick={viewOnly ? undefined : () => onDownload(row)}
      >
        {!viewOnly && (
          <td className="ps-4 py-2.5" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              id={`invoice-${rowKey}`}
              checked={isChecked}
              onChange={(checked) => onToggle(rowKey, checked)}
            />
          </td>
        )}
        <td
          className={clsx(
            'py-2.5 text-sm text-neutral-primary',
            viewOnly ? 'ps-6 pe-4' : 'px-4'
          )}
        >
          {row?.invoiceName}
        </td>
        <td className="px-4 py-2.5 text-sm text-neutral-primary">
          {formatCurrency(row.amount, row.currency)}
        </td>
        {!viewOnly && (
          <td className="px-4 py-2.5 text-sm text-neutral-primary">
            {row?.student_limit}
          </td>
        )}
        <td className="px-4 py-2.5 text-sm text-neutral-primary">
          {formatDate(row?.start_date)}
        </td>
        <td className="px-4 py-2.5">
          <PaidBadge label={paidLabel} />
        </td>
        {!viewOnly && (
          <td className="px-4 py-2.5 text-center">
            <Button
              variant="unstyled"
              className="p-1 hover:bg-neutral-light-100 rounded cursor-pointer mx-auto"
              icon={
                <IconDownload size={20} className="text-neutral-secondary" />
              }
              onClick={(e) => {
                e.stopPropagation();
                onDownload(row);
              }}
            />
          </td>
        )}
      </tr>
      {children.map((detail, idx) => (
        <ChildRow
          key={detail.id}
          detail={detail}
          isLast={idx === children.length - 1}
          paidLabel={paidLabel}
          isChecked={isChildChecked(detail.id)}
          viewOnly={viewOnly}
          formatDate={formatDate}
          onToggle={onToggle}
          onDownload={onDownload}
        />
      ))}
    </>
  );
};

const BillingHistoryTable = ({
  billingHistory,
  customer,
  viewOnly = false,
}: IBillingHistoryTableProps) => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const paidLabel = formatText('settings.subscription.paid');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key)
    );
  };

  const handleDownloadRow = (
    row: TBillingHistoryItem | TSubscriptionDetailItem
  ) => {
    const record = buildBillingRecord(row, formatDate);
    downloadInvoices([record], customer, `${record.invoiceNumber}.pdf`);
  };

  const handleDownloadAll = () => {
    const records: TBillingRecord[] = [];
    billingHistory.forEach((row) => {
      records.push(buildBillingRecord(row, formatDate));
      row.subscriptionDetails?.forEach((detail) => {
        records.push(buildBillingRecord(detail, formatDate));
      });
    });
    downloadInvoices(records, customer, 'invoices-all.pdf');
  };

  const handleDownloadSelected = () => {
    const records: TBillingRecord[] = [];
    billingHistory.forEach((row, index) => {
      if (selectedKeys.includes(`row-${index}`)) {
        records.push(buildBillingRecord(row, formatDate));
      }
      row.subscriptionDetails?.forEach((detail) => {
        if (selectedKeys.includes(detail.id)) {
          records.push(buildBillingRecord(detail, formatDate));
        }
      });
    });
    downloadInvoices(
      records,
      customer,
      `invoices-${selectedKeys.length}.pdf`
    ).then(() => setSelectedKeys([]));
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
          {formatText('settings.subscription.billing-history')}
        </h3>
        {!viewOnly && (
          <div className="flex items-center gap-2">
            {selectedKeys.length > 0 && (
              <Button
                label={formatText('settings.subscription.download-selected', {
                  count: selectedKeys.length,
                })}
                variant="primary"
                icon={<IconDownload size={16} />}
                onClick={handleDownloadSelected}
              />
            )}
            {billingHistory.length > 0 && (
              <Button
                label={formatText('settings.subscription.download-all')}
                variant="outline"
                icon={<IconDownload size={16} />}
                onClick={handleDownloadAll}
              />
            )}
          </div>
        )}
      </div>

      {billingHistory.length === 0 ? (
        <div className="border border-neutral-light-200 rounded-xl p-10">
          <EmptyState
            className="h-auto!"
            title={formatText('settings.subscription.no-billing-history-title')}
            description={formatText(
              'settings.subscription.no-billing-history-description'
            )}
          />
        </div>
      ) : (
        <div className="border border-neutral-light-200 rounded-2xl overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-neutral-light-100">
                {!viewOnly && <th className="w-10 ps-4 py-3 rounded-tl-2xl" />}
                <th
                  className={clsx(
                    'text-start py-3 text-sm font-normal text-neutral-secondary',
                    viewOnly ? 'ps-6 pe-4 rounded-tl-2xl' : 'px-4'
                  )}
                >
                  {formatText('settings.subscription.invoices')}
                </th>
                <th className="text-start px-4 py-3 text-sm font-normal text-neutral-secondary">
                  {formatText('settings.subscription.amount')}
                </th>
                {!viewOnly && (
                  <th className="text-start px-4 py-3 text-sm font-normal text-neutral-secondary">
                    {formatText('settings.subscription.no-of-students')}
                  </th>
                )}
                <th className="text-start px-4 py-3 text-sm font-normal text-neutral-secondary">
                  {formatText('settings.subscription.date')}
                </th>
                <th
                  className={clsx(
                    'text-start px-4 py-3 text-sm font-normal text-neutral-secondary',
                    viewOnly && 'rounded-tr-2xl'
                  )}
                >
                  {formatText('settings.subscription.status')}
                </th>
                {!viewOnly && <th className="w-14 rounded-tr-2xl" />}
              </tr>
            </thead>
            <tbody>
              {billingHistory?.map((row, index) => (
                <BillingRow
                  key={index}
                  row={row}
                  rowIndex={index}
                  paidLabel={paidLabel}
                  isChecked={selectedKeys.includes(`row-${index}`)}
                  viewOnly={viewOnly}
                  isChildChecked={(id) => selectedKeys.includes(id)}
                  formatDate={formatDate}
                  onToggle={toggleRow}
                  onDownload={handleDownloadRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BillingHistoryTable;
